import type { OrderNode } from '../graphql/queries/orders';
import {
  formatBookingDate,
  formatStatusLabel,
  getCustomerName,
  normalizeOrderStatus,
  resolvePaymentStatus,
} from './bookingOrderHelpers';
import {
  formatDashboardMoney,
  getOrderDueDate,
  getOrderStatusBucket,
  isOverdueInvoice,
  isOutstandingPayment,
  parseAmount,
} from './dashboardMetrics';

export type HeaderNotificationType =
  | 'pending'
  | 'unpaid'
  | 'overdue'
  | 'cancel_request';

export interface HeaderNotification {
  id: string;
  type: HeaderNotificationType;
  title: string;
  message: string;
  to: string;
  createdAt: string;
  orderId: number;
  icon: string;
  tone: 'primary' | 'success' | 'warning' | 'danger' | 'info';
}

function timeLabel(dateStr?: string): string {
  if (!dateStr) return '';
  const time = new Date(dateStr).getTime();
  if (!Number.isFinite(time)) return formatBookingDate(dateStr);
  const diffMs = Date.now() - time;
  const mins = Math.floor(diffMs / 60000);
  if (mins < 1) return 'Just now';
  if (mins < 60) return `${mins} min ago`;
  const hours = Math.floor(mins / 60);
  if (hours < 24) return `${hours} hour${hours === 1 ? '' : 's'} ago`;
  const days = Math.floor(hours / 24);
  if (days < 7) return `${days} day${days === 1 ? '' : 's'} ago`;
  return formatBookingDate(dateStr);
}

export function formatNotificationTime(dateStr?: string): string {
  return timeLabel(dateStr);
}

/**
 * Build actionable header alerts from WooCommerce orders.
 * One order may produce multiple alerts (e.g. pending + unpaid).
 */
export function buildOrderNotifications(
  orders: OrderNode[],
  now = new Date()
): HeaderNotification[] {
  const items: HeaderNotification[] = [];

  orders.forEach((order) => {
    const orderId = order.databaseId;
    if (!orderId) return;

    const status = normalizeOrderStatus(order.status);
    const bucket = getOrderStatusBucket(order.status);
    const payment = resolvePaymentStatus(order);
    const customer = getCustomerName(order);
    const amount = formatDashboardMoney(parseAmount(order.total), order.currency);
    const ref = `#${order.orderNumber || orderId}`;
    const createdAt = order.date || now.toISOString();
    const detailPath = `/orders/${orderId}`;

    if (status === 'CANCEL_REQUEST') {
      items.push({
        id: `cancel_request-${orderId}`,
        type: 'cancel_request',
        title: 'Cancel request',
        message: `${customer} · ${ref} · ${amount}`,
        to: detailPath,
        createdAt,
        orderId,
        icon: 'bx bx-x-circle',
        tone: 'warning',
      });
    } else if (bucket === 'pending') {
      items.push({
        id: `pending-${orderId}`,
        type: 'pending',
        title: 'Pending booking',
        message: `${customer} · ${ref} · ${formatStatusLabel(order.status)} · ${amount}`,
        to: detailPath,
        createdAt,
        orderId,
        icon: 'bx bx-time-five',
        tone: 'primary',
      });
    }

    if (
      isOutstandingPayment(payment) &&
      bucket !== 'cancelled' &&
      status !== 'REFUNDED' &&
      status !== 'FAILED'
    ) {
      if (isOverdueInvoice(order, now)) {
        const due = getOrderDueDate(order);
        items.push({
          id: `overdue-${orderId}`,
          type: 'overdue',
          title: 'Overdue invoice',
          message: `${customer} · ${ref} · ${amount}${
            due ? ` · due ${formatBookingDate(due.toISOString())}` : ''
          }`,
          to: `/invoices-detail/${orderId}`,
          createdAt,
          orderId,
          icon: 'bx bx-error-circle',
          tone: 'danger',
        });
      } else {
        items.push({
          id: `unpaid-${orderId}`,
          type: 'unpaid',
          title: 'Unpaid invoice',
          message: `${customer} · ${ref} · ${amount} · ${payment}`,
          to: `/invoices-detail/${orderId}`,
          createdAt,
          orderId,
          icon: 'bx bx-receipt',
          tone: 'warning',
        });
      }
    }
  });

  return items.sort((a, b) => {
    const aTime = new Date(a.createdAt).getTime();
    const bTime = new Date(b.createdAt).getTime();
    return (Number.isFinite(bTime) ? bTime : 0) - (Number.isFinite(aTime) ? aTime : 0);
  });
}

export function getNotificationReadStorageKey(userId?: string | number | null): string {
  return `ngk_notification_read_${userId || 'anon'}`;
}

export function loadReadNotificationIds(storageKey: string): Set<string> {
  if (typeof window === 'undefined') return new Set();
  try {
    const raw = window.localStorage.getItem(storageKey);
    if (!raw) return new Set();
    const parsed = JSON.parse(raw);
    if (!Array.isArray(parsed)) return new Set();
    return new Set(parsed.filter((id) => typeof id === 'string'));
  } catch {
    return new Set();
  }
}

export function saveReadNotificationIds(storageKey: string, ids: Set<string>): void {
  if (typeof window === 'undefined') return;
  try {
    window.localStorage.setItem(storageKey, JSON.stringify(Array.from(ids)));
  } catch {
    // Ignore quota / private mode failures.
  }
}
